import { API, unwrapData } from '../axios-instance'
import { Comment, ThreadType } from '../comment'
import { InitiateUploadParams, MediaObjectUpload } from '../media'
import { RecognitionReaction } from '../recognition'
import {
  CreateCommentParams,
  CreateReactionParams,
  CursorResult,
  GetCommentsParams,
  ListCommentsReactionsParams,
  ListReactionsParams,
  PageResult,
  Reaction,
  UpdateWallPostCommentParams,
} from '../shared'
import {
  CreateAlbumPostParams,
  CreateSpacePostPhotoParams,
  CreateSpacesParams,
  CreateWallPostParams,
  GetMySpaceParams,
  GetSpacePostsParams,
  GetWidgetAlbumParams,
  ListAdminSpacesParams,
  ListMySpacesParams,
  ListSpacePostPhotosParams,
  Post,
  Space,
  SpaceAlbumPostEmbeds,
  SpacePostEmbeds,
  SpacePostPhoto,
  SpacePostPhotoEmbeds,
  Subscriber,
} from './types'

export const listAdminSpaces = async (params?: ListAdminSpacesParams) => {
  // NOTE: This is a BE request to filter out archived spaces at a low level.
  // The archive feature was built with flexibility in mind (instead of delete)
  // so that an unarchive feature can be added in the future if needed.
  return API.get<PageResult<Space>>('spaces/spaces', {
    params: { ...params, archived: false },
  }).then(unwrapData)
}

export const getMySpace = async (spaceId: string, params: GetMySpaceParams) => {
  return await API.get<Space>(`spaces/spaces/my/${spaceId}`, { params }).then(unwrapData)
}

export const getSpacePosts = async (spaceId: string, params: GetSpacePostsParams) => {
  return await API.get<CursorResult<Post>>(`spaces/spaces/my/${spaceId}/posts`, {
    params,
  }).then(unwrapData)
}

export const getSpaceAlbums = async (spaceId: string, params: GetSpacePostsParams) => {
  return await API.get<CursorResult<Post>>(`spaces/spaces/my/${spaceId}/albums`, {
    params,
  }).then(unwrapData)
}

export const listMySpaces = async (params?: ListMySpacesParams) => {
  return API.get<PageResult<Space>>('spaces/spaces/my', { params }).then(unwrapData)
}

export const initiateIconMediaUpload = async (params: InitiateUploadParams) => {
  return await API.post<MediaObjectUpload>('/spaces/uploads/spaces/icon', params).then(unwrapData)
}

export const initiateBannerMediaUpload = async (params: InitiateUploadParams) => {
  return await API.post<MediaObjectUpload>('/spaces/uploads/spaces/banner', params).then(unwrapData)
}

export const initiateSpacePostPhotoUpload = async (params: InitiateUploadParams) => {
  return await API.post<MediaObjectUpload>('/spaces/uploads/photo', params).then(unwrapData)
}

export const createSpace = async (params: CreateSpacesParams) => {
  return await API.post<Space>('/spaces/spaces', params).then(unwrapData)
}

export const editSpace = async (spaceId: string, params: CreateSpacesParams) => {
  return await API.patch<Space>(`/spaces/spaces/${spaceId}`, params).then(unwrapData)
}

export const archiveSpace = async (spaceId: string) => {
  return await API.post<boolean>(`/spaces/spaces/${spaceId}/archive`).then(unwrapData)
}

export const updateSpaceSubscription = async (spaceId: string, subscribed: boolean) => {
  return await API.put<Subscriber>(`/spaces/spaces/${spaceId}/subscribers`, { subscribed }).then(
    unwrapData,
  )
}

export const updateMarkAsSeen = async (spaceId: string) => {
  return await API.put<boolean>(`/spaces/spaces/${spaceId}/seen`).then(unwrapData)
}

export const createSpacePost = async (
  spaceId: string,
  params: CreateWallPostParams | CreateAlbumPostParams,
) => {
  return await API.post<Post>(`/spaces/spaces/${spaceId}/posts`, params).then(unwrapData)
}

export const editSpacePost = async (
  spaceId: string,
  postId: string,
  params: CreateWallPostParams | CreateAlbumPostParams,
) => {
  return await API.patch<Post>(`/spaces/spaces/${spaceId}/posts/${postId}`, params).then(unwrapData)
}

export const createSpacePostComment = async (
  spaceId: string,
  { body, id: postId, tenantMemberId, threadId, gifs }: CreateCommentParams,
) => {
  return await API.post<Comment>(`spaces/spaces/${spaceId}/posts/${postId}/comments`, {
    body,
    tenantMemberId,
    threadId,
    threadType: ThreadType.POST,
    gifs: gifs || [],
  }).then(unwrapData)
}

export const editSpacePostComment = async (
  spaceId: string,
  postId: string,
  params: UpdateWallPostCommentParams,
  commentId: string,
) => {
  return await API.patch<Comment>(
    `/spaces/spaces/${spaceId}/posts/${postId}/comments/${commentId}`,
    params,
  ).then(unwrapData)
}

export const listSpacePostComments = async (
  spaceId: string,
  postId: string,
  params?: GetCommentsParams,
) => {
  return await API.get<PageResult<Comment>>(`/spaces/spaces/${spaceId}/posts/${postId}/comments`, {
    params,
  }).then(unwrapData)
}

export const listSpacePostReactions = async (
  spaceId: string,
  postId: string,
  params: ListReactionsParams,
) => {
  return await API.get<PageResult<RecognitionReaction>>(
    `/spaces/spaces/${spaceId}/posts/${postId}/reactions`,
    { params },
  ).then(unwrapData)
}

export const listSpacePostCommentReactions = async (
  spaceId: string,
  postId: string,
  commentId: string,
  params: ListCommentsReactionsParams,
) => {
  return await API.get<PageResult<Reaction>>(
    `/spaces/spaces/${spaceId}/posts/${postId}/comments/${commentId}/reactions`,
    { params },
  ).then(unwrapData)
}

export const reactToSpacePost = async (spaceId: string, params: CreateReactionParams) => {
  return await API.post<Reaction>(
    `/spaces/spaces/${spaceId}/posts/${params.postId}/reactions`,
    params,
  ).then(unwrapData)
}

export const removeSpacePostReaction = async (spaceId: string, postId: string) => {
  return await API.delete<boolean>(`/spaces/spaces/${spaceId}/posts/${postId}/reactions`).then(
    unwrapData,
  )
}

export const reactToSpacePostComment = async (
  spaceId: string,
  { reaction, postId, commentId }: CreateReactionParams,
) => {
  return await API.put<RecognitionReaction>(
    `/spaces/spaces/${spaceId}/posts/${postId}/comments/${commentId}/reactions`,
    {
      reaction,
    },
  ).then(unwrapData)
}

export const removeSpacePostCommentReaction = async (
  spaceId: string,
  { postId, commentId }: Pick<CreateReactionParams, 'postId' | 'commentId'>,
) => {
  return await API.delete<boolean>(
    `/spaces/spaces/${spaceId}/posts/${postId}/comments/${commentId}/reactions`,
  ).then(unwrapData)
}

export const getSpacePost = async (spaceId: string, postId: string, embeds: SpacePostEmbeds[]) => {
  return await API.get<Post>(`/spaces/spaces/my/${spaceId}/posts/${postId}`, {
    params: {
      embeds,
    },
  }).then(unwrapData)
}

export const getMySpaceAlbum = async (
  spaceId: string,
  postId: string,
  embeds?: SpaceAlbumPostEmbeds[],
) => {
  return await API.get<Post>(`/spaces/spaces/my/${spaceId}/albums/${postId}`, {
    params: {
      embeds,
    },
  }).then(unwrapData)
}

export const deleteSpacePostComment = async (
  spaceId: string,
  postId: string,
  commentId: string,
) => {
  return await API.delete<boolean>(
    `/spaces/spaces/${spaceId}/posts/${postId}/comments/${commentId}`,
  ).then(unwrapData)
}

export const archivePost = async (postId: string, spaceId: string) => {
  return await API.post<boolean>(`/spaces/spaces/${spaceId}/posts/${postId}/archive`).then(
    unwrapData,
  )
}

export const pinPost = async (postId: string, spaceId: string) => {
  return await API.post<boolean>(`/spaces/spaces/${spaceId}/posts/${postId}/pin`, {
    pinned: true,
  }).then(unwrapData)
}

export const unPinPost = async (postId: string, spaceId: string) => {
  return await API.post<boolean>(`/spaces/spaces/${spaceId}/posts/${postId}/pin`, {
    pinned: false,
  }).then(unwrapData)
}
export const createSpacePostPhoto = async (
  spaceId: string,
  postId: string,
  params: CreateSpacePostPhotoParams,
) => {
  return await API.post<boolean>(`/spaces/spaces/${spaceId}/posts/${postId}/photos`, params).then(
    unwrapData,
  )
}

export const listSpacePostPhotos = async (
  spaceId: string,
  postId: string,
  params?: ListSpacePostPhotosParams,
) => {
  return await API.get<CursorResult<SpacePostPhoto>>(
    `/spaces/spaces/my/${spaceId}/albums/${postId}/photos`,
    { params },
  ).then(unwrapData)
}

export const archiveAlbumPhotos = async (spaceId: string, postId: string, photoIds: string[]) => {
  return await API.post<boolean>(`/spaces/spaces/${spaceId}/albums/${postId}/photos/archive`, {
    photoIds,
  }).then(unwrapData)
}

export const getWidgetAlbumPhotos = async (spaceId: string, params: GetWidgetAlbumParams) => {
  return await API.get<SpacePostPhoto[]>(`/spaces/spaces/my/${spaceId}/photos/recent`, {
    params,
  }).then(unwrapData)
}

export const getSpacePostPhoto = async (
  spaceId: string,
  photoId: string,
  embeds: SpacePostPhotoEmbeds[],
) => {
  return await API.get<SpacePostPhoto>(`/spaces/spaces/my/${spaceId}/photos/${photoId}`, {
    params: {
      embeds,
    },
  }).then(unwrapData)
}

export const reactToSpacePostPhoto = async (
  spaceId: string,
  photoId: string,
  params: CreateReactionParams,
) => {
  return await API.post<Reaction>(
    `/spaces/spaces/${spaceId}/photos/${photoId}/reactions`,
    params,
  ).then(unwrapData)
}

export const removeSpacePostPhotoReaction = async (spaceId: string, photoId: string) => {
  return await API.delete<boolean>(`/spaces/spaces/${spaceId}/photos/${photoId}/reactions`).then(
    unwrapData,
  )
}

export const createSpacePostPhotoComment = async (
  spaceId: string,
  { body, id: photoId, tenantMemberId, threadId, gifs }: CreateCommentParams,
) => {
  return await API.post<Comment>(`spaces/spaces/${spaceId}/photos/${photoId}/comments`, {
    body,
    tenantMemberId,
    threadId,
    threadType: ThreadType.POST,
    gifs: gifs || [],
  }).then(unwrapData)
}

export const listSpacePostPhotoComments = async (
  spaceId: string,
  photoId: string,
  params?: GetCommentsParams,
) => {
  return await API.get<PageResult<Comment>>(
    `/spaces/spaces/${spaceId}/photos/${photoId}/comments`,
    { params },
  ).then(unwrapData)
}

export const editSpacePostPhotoComment = async (
  spaceId: string,
  photoId: string,
  params: UpdateWallPostCommentParams,
  commentId: string,
) => {
  return await API.patch<Comment>(
    `/spaces/spaces/${spaceId}/photos/${photoId}/comments/${commentId}`,
    params,
  ).then(unwrapData)
}

export const deleteSpacePostPhotoComment = async (
  spaceId: string,
  photoId: string,
  commentId: string,
) => {
  return await API.delete<boolean>(
    `/spaces/spaces/${spaceId}/photos/${photoId}/comments/${commentId}`,
  ).then(unwrapData)
}

export const reactToSpacePostPhotoComment = async (
  spaceId: string,
  photoId: string,
  { reaction, commentId }: CreateReactionParams,
) => {
  return await API.put<RecognitionReaction>(
    `/spaces/spaces/${spaceId}/photos/${photoId}/comments/${commentId}/reactions`,
    { reaction },
  ).then(unwrapData)
}

export const removeSpacePostPhotoCommentReaction = async (
  spaceId: string,
  photoId: string,
  commentId: string,
) => {
  return await API.delete<boolean>(
    `/spaces/spaces/${spaceId}/photos/${photoId}/comments/${commentId}/reactions`,
  ).then(unwrapData)
}

export const listPhotoReactions = async (
  spaceId: string,
  photoId: string,
  params: ListReactionsParams,
) => {
  return await API.get<PageResult<RecognitionReaction>>(
    `/spaces/spaces/${spaceId}/photos/${photoId}/reactions`,
    {
      params,
    },
  ).then(unwrapData)
}

export const listSpacePostPhotoCommentReactions = async (
  spaceId: string,
  photoId: string,
  commentId: string,
  params: ListCommentsReactionsParams,
) => {
  return await API.get<PageResult<Reaction>>(
    `/spaces/spaces/${spaceId}/photos/${photoId}/comments/${commentId}/reactions`,
    { params },
  ).then(unwrapData)
}
